import * as React from 'react';
import axios from "axios";
import { useState, useEffect, useContext } from 'react';
import { AuthContext } from "../Auth/AuthContext";
import Button from '@mui/material/Button';
import { SvgIcon } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import { FileUploader } from "react-drag-drop-files";

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import ImageIcon from '@mui/icons-material/Image';
import { alpha } from '@mui/material/styles';
import AttachmentIcon from '@mui/icons-material/Attachment';

import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';

import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import { updateProcessingRequestIds, extractIdsByTitles } from "../utils/requestManagement";

export default function EditFileDialog({ setOpen, open, data, reloadProjects, processings }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { jwt, cloudinary_cloud, cloudinary_preset } = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);
  const [imageSelected, setImageSelected] = useState("");
  const imageTypes = ["PNG", "png", "JPG", "jpg", "JPEG", "jpeg"];

  const handleImageChange = (file) => {
    setImageSelected(file);
  };

  // Tree view states (selected items are string IDs for the tree view)
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  // New state: initialProcessingRequests holds the objects from Strapi.
  const [initialProcessingRequests, setInitialProcessingRequests] = useState([]);

  const handleClose = () => {
    setOpen(false);
  };

  // Local description state.
  const [descriptionNew, setDescriptionNew] = useState(data.attributes.description);
  useEffect(() => {
    setDescriptionNew(data.attributes.description);
  }, [data]);

  const uploadImage = async (imageSelected) => {
    const formData = new FormData();
    formData.append("file", imageSelected);
    formData.append("upload_preset", cloudinary_preset);
    
    const response = await axios.post(
      `https://api.cloudinary.com/v1_1/${cloudinary_cloud}/image/upload`,
      formData
    );
    return response.data.secure_url;
  };
    
  // updateData now updates the processing_requests field with the new list of Strapi IDs.
  const updateData = async (description, imageUrl = null) => {
    // initialProcessingList: use the stored initialProcessingRequests (with the actual Strapi id references)
    const initialProcessingList = initialProcessingRequests;
    // processingList: build from the current tree selection (selectedItems) using your helper.
    const processingList = extractIdsByTitles(selectedItems, processings);
    //console.log("Initial processing requests:", initialProcessingList);
    //console.log("New processing requests:", processingList);
    
    const processingRequestIds = await updateProcessingRequestIds(jwt, initialProcessingList, processingList);
    //console.log("New processing request IDs:", processingRequestIds);
    
    const dataForm = new FormData();
    const dataNew = {
      description: description,
      processing_requests: processingRequestIds  // Save the array of processing request IDs
    };
    if (imageUrl) {
      dataNew.imageUrl = imageUrl;
    }
    dataForm.append('data', JSON.stringify(dataNew));
        
    await axios.put(`${global.API_URL}/api/datas/${data.id}`, dataForm, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      }
    });
  };
    
  const handleEdit = async () => {
    setIsLoading(true);
    try {
      let optimizeImage = null;
      if (typeof imageSelected.name === 'string') {
        optimizeImage = await uploadImage(imageSelected);
      }
      await updateData(descriptionNew, optimizeImage);
      setOpen(false);
      enqueueSnackbar(`${t("SuccessEditFile")}`, { variant: 'success' });
      reloadProjects();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${t("ErrorEditFile")}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Controlled selection handler for tree view.
  const handleSelectedItemsChange = (event, ids) => {
    let newSelected = [...ids];
    // Example: add default suboption logic if needed.
    setSelectedItems(newSelected);
  };

  // Helper function to create a valid tree item id from a title.
  const createItemId = (title) =>
    title.toLowerCase().replace(/\s+/g, '-');

  // Compute the initial tree selection and processing requests from the data.
  useEffect(() => {
    const initialTreeSelection = [];
    const initialPRs = [];
    if (
      data?.attributes?.processing_requests &&
      data.attributes.processing_requests.data &&
      data.attributes.processing_requests.data.length > 0
    ) {
      data.attributes.processing_requests.data.forEach((req) => {
        if (req.attributes.processing && req.attributes.processing.data) {
          const procData = req.attributes.processing.data;
          const procTitle = procData.attributes.title;
          // Push the tree id for the parent.
          initialTreeSelection.push(createItemId(procTitle));
          let subIds = [];
          if (
            req.attributes.subprocessings &&
            req.attributes.subprocessings.data &&
            req.attributes.subprocessings.data.length > 0
          ) {
            req.attributes.subprocessings.data.forEach((sub) => {
              // Push the tree id for each subprocessing.
              initialTreeSelection.push(createItemId(sub.attributes.title));
              subIds.push(sub.id); // collect the actual Strapi id
            });
          }
          // Push the complete object for this processing request.
          initialPRs.push({
            id: req.id,                // processing_request record id
            processing: procData.id,     // processing id from the relation
            subprocessings: subIds,       // array of subprocessing ids
            done: req.attributes.done
          });
        }
      });
    }
    setInitialProcessingRequests(initialPRs);
    setSelectedItems(initialTreeSelection);

    // Compute expanded items: for each processing that has subProcessings, if any child is selected, expand the parent.
    const initialExpanded = [];
    processings.forEach((proc) => {
      if (proc.subProcessings && proc.subProcessings.length > 0) {
        const parentId = createItemId(proc.title);
        const hasChildSelected = proc.subProcessings.some(
          (sub) => initialTreeSelection.includes(createItemId(sub.title))
        );
        if (hasChildSelected) {
          initialExpanded.push(parentId);
        }
      }
    });
    setExpandedItems(initialExpanded);
  }, [data, processings]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <DialogTitle>
            <Typography variant="h4" component="div">
              {t("EditData")}
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
                position: 'absolute',
                right: 16,
                top: 16,
                color: theme.palette.grey[500],
            })}
            >
            <CancelIcon />
            </IconButton>
          <DialogContent>
            <DialogContentText component="div">
              <Typography color="text.secondary" variant="body2">
                {t("PleaseModifyData")}
              </Typography>
            </DialogContentText>
            <Stack spacing={3} sx={{ mt: 3 }}>
              <Stack spacing={1}>
                <Typography color="text.secondary" variant="h6">
                  {t("NewDescription")}
                </Typography>
                <TextField
                  fullWidth
                  name="description"
                  type="text"
                  multiline
                  rows={2}
                  value={descriptionNew}
                  onChange={(event) => setDescriptionNew(event.target.value)}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography color="text.secondary" variant="h6">
                  {t("Image")}
                </Typography>
                <FileUploader
                  handleChange={handleImageChange}
                  multiple={false}
                  name="image"
                  types={imageTypes}
                  hoverTitle=" "
                  children={
                    <Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          borderColor: '#E5E7EB',
                          borderStyle: 'solid',
                          borderWidth: 1,
                          '&:hover': { backgroundColor: alpha('#111927', 0.04) }
                        }}
                      >
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          style={{ minHeight: '80px', padding: 20 }}
                        >
                          <Stack alignItems="center" direction="row" spacing={2}>
                            <ImageIcon style={{ color: 'gray' }} />
                            <Typography variant="body2" color="text.secondary">
                              {t("dragndrop")}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Paper>
                    </Box>
                  }
                />
                {imageSelected && (
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <AttachmentIcon style={{ color: 'gray' }} />
                    <Typography variant="body2" color="text.secondary">
                      <b>{imageSelected.name}</b>
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography color="text.secondary" variant="h6">
                  {t("ProcessingOptions")}
                </Typography>
                <SimpleTreeView
                  multiSelect
                  checkboxSelection
                  selectedItems={selectedItems}
                  onSelectedItemsChange={handleSelectedItemsChange}
                  expandedItems={expandedItems}
                  onExpandedItemsChange={(e, ids) => setExpandedItems(ids)}
                >
                  {processings.map((processing) => {
                    const parentId = createItemId(processing.title);
                     // Find the corresponding processing request from initialProcessingRequests.
                    const pr = initialProcessingRequests.find(req => req.processing === processing.id);
                    const isDisabled = pr ? pr.done === true : false;
                    if (processing.subProcessings && processing.subProcessings.length > 0) {
                      return (
                        <TreeItem key={processing.id} itemId={parentId} label={t(processing.title)} disabled={isDisabled}>
                          {processing.subProcessings.map((sub) => {
                            const subId = createItemId(sub.title);
                            return (
                              <TreeItem key={sub.id} itemId={subId} label={t(sub.title)} disabled={isDisabled} />
                            );
                          })}
                        </TreeItem>
                      );
                    }
                    return (
                      <TreeItem key={processing.id} itemId={parentId} label={t(processing.title)} disabled={isDisabled}/>
                    );
                  })}
                </SimpleTreeView>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              startIcon={
                <SvgIcon fontSize="medium">
                  <SaveIcon />
                </SvgIcon>
              }
              variant="contained"
              onClick={handleEdit}
            >
              {isLoading ? `${t("Saving")} ...` : `${t("Save")}`}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
